import { useState, useEffect, useCallback } from "react";

export type ScheduleFilter = "has_schedule" | "no_schedule" | "all";
export type RegularStudentSortField = "name" | "code" | "hours" | "weekly_scheduled_hours" | "estimated_hours_depletion_date" | "last_class_date" | "has_classes_last_30_days";
export type SortOrder = "asc" | "desc";

interface FilterState {
  search: string;
  scheduleFilter: ScheduleFilter;
  sortField: RegularStudentSortField;
  sortOrder: SortOrder;
}

const STORAGE_KEY = "regular-students-filters";

const DEFAULTS: FilterState = {
  search: "",
  scheduleFilter: "has_schedule",
  sortField: "hours",
  sortOrder: "asc",
};

function loadFromStorage(): FilterState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

export function useRegularStudentFilters() {
  const [state, setState] = useState<FilterState>(loadFromStorage);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const setSearch = useCallback((search: string) => setState((s) => ({ ...s, search })), []);
  const setScheduleFilter = useCallback((scheduleFilter: ScheduleFilter) => setState((s) => ({ ...s, scheduleFilter })), []);

  const toggleSort = useCallback((field: RegularStudentSortField) => {
    setState((s) => ({
      ...s,
      sortField: field,
      sortOrder: s.sortField === field && s.sortOrder === "asc" ? "desc" : "asc",
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setState(DEFAULTS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const isDefault =
    state.search === DEFAULTS.search &&
    state.scheduleFilter === DEFAULTS.scheduleFilter &&
    state.sortField === DEFAULTS.sortField &&
    state.sortOrder === DEFAULTS.sortOrder;

  return { ...state, setSearch, setScheduleFilter, toggleSort, resetFilters, isDefault };
}
